import React, { useEffect, useState } from "react";
import { useFormikContext } from "formik";
import { FiFileText } from "react-icons/fi";

import { theme } from "../../styles/theme";

export type NfseNacionalCardMode = "array" | "boolean";

interface NfseNacionalCardProps {
  /** Nome do campo no Formik. Default: "nfseNacional". */
  name?: string;
  /** Valor inicial (empresa já emite NFS-e Nacional?). */
  initialChecked?: boolean;
  /**
   * Formato do valor gravado no Formik:
   * - "array": ["true"] | []  (usado pelas factories antigas / makeCompany)
   * - "boolean": true | false (usado pelo FieldsCompany / form-factory novo)
   */
  mode?: NfseNacionalCardMode;
  /** Desabilita a interação (ex.: telas de visualização). */
  disabled?: boolean;
}

const ACTIVE = theme.colors.green;

const NfseNacionalCard: React.FC<NfseNacionalCardProps> = ({
  name = "nfseNacional",
  initialChecked = false,
  mode = "boolean",
  disabled = false,
}) => {
  const { values, setFieldValue } = useFormikContext<any>();
  const [checked, setChecked] = useState<boolean>(initialChecked);

  const toStoreValue = (isChecked: boolean): any => {
    if (mode === "array") {
      return isChecked ? ["true"] : [];
    }
    return isChecked;
  };

  const readFormikChecked = (raw: any): boolean =>
    mode === "array" ? Boolean(raw?.length) : Boolean(raw);

  // Semeia o valor no Formik ao montar e sempre que o valor inicial mudar
  // (ex.: troca de empresa). Toggles do usuário não disparam este efeito,
  // pois initialChecked permanece o mesmo entre renders.
  useEffect(() => {
    setChecked(initialChecked);
    setFieldValue(name, toStoreValue(initialChecked));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialChecked, name]);

  // Mantém o estado local em sincronia caso o valor mude externamente.
  useEffect(() => {
    const raw = values?.[name];

    // Quando o Formik reinicializa (enableReinitialize), o valor deste campo
    // volta para o placeholder vazio do getInitialValues ("" / undefined).
    // Nesse caso não é uma alteração real do usuário: re-semeamos a partir do
    // initialChecked para não perder o valor vindo do banco.
    const isUninitialized = raw === undefined || raw === null || raw === "";
    if (isUninitialized) {
      setChecked(initialChecked);
      setFieldValue(name, toStoreValue(initialChecked));
      return;
    }

    const formikChecked = readFormikChecked(raw);
    if (formikChecked !== checked) {
      setChecked(formikChecked);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(values?.[name]), initialChecked, name]);

  const handleToggle = (): void => {
    if (disabled) return;
    const next = !checked;
    setChecked(next);
    setFieldValue(name, toStoreValue(next));
  };

  return (
    <div
      role="switch"
      aria-checked={checked}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onClick={handleToggle}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleToggle();
        }
      }}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "16px",
        width: "100%",
        padding: "16px 20px",
        borderRadius: "10px",
        cursor: disabled ? "not-allowed" : "pointer",
        userSelect: "none",
        opacity: disabled ? 0.6 : 1,
        border: checked
          ? `1px solid ${ACTIVE}`
          : "1px dashed rgba(255, 255, 255, 0.25)",
        background: checked
          ? "rgba(0, 194, 71, 0.12)"
          : "rgba(255, 255, 255, 0.04)",
        boxShadow: checked ? "0 0 0 3px rgba(0, 194, 71, 0.12)" : "none",
        transition: "all 0.2s ease-in-out",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          width: "42px",
          height: "42px",
          borderRadius: "50%",
          background: checked ? ACTIVE : "rgba(255, 255, 255, 0.1)",
          color: checked ? "#fff" : "rgba(255, 255, 255, 0.6)",
          transition: "all 0.2s ease-in-out",
        }}
      >
        <FiFileText size={20} />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <strong
          style={{
            display: "block",
            fontSize: "15px",
            color: "#fff",
            marginBottom: "2px",
          }}
        >
          NFS-e Nacional
        </strong>
        <span
          style={{
            fontSize: "13px",
            color: checked ? ACTIVE : "rgba(255, 255, 255, 0.6)",
            transition: "color 0.2s ease-in-out",
          }}
        >
          {checked
            ? "A empresa está habilitada para emissão de NFS-e Nacional."
            : "Marque se a empresa emite NFS-e Nacional."}
        </span>
      </div>

      {/* Toggle switch */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: checked ? "flex-end" : "flex-start",
          flexShrink: 0,
          width: "48px",
          height: "26px",
          padding: "3px",
          borderRadius: "13px",
          background: checked ? ACTIVE : "rgba(255, 255, 255, 0.25)",
          transition: "all 0.2s ease-in-out",
        }}
      >
        <div
          style={{
            width: "20px",
            height: "20px",
            borderRadius: "50%",
            background: "#fff",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.3)",
            transition: "all 0.2s ease-in-out",
          }}
        />
      </div>
    </div>
  );
};

export default NfseNacionalCard;
